import {
  createInvite,
  createShareLink,
  fetchProfilesByIds,
  getFilePrivate,
  getMyMembership,
  listInvites,
  listMembers,
  listShareLinks,
  type ProfileRow,
  personLabel,
  refreshShareLink,
  removeMember,
  revokeInvite,
  revokeShareLink,
  setFilePrivate,
  setMemberProjectWide,
  type WorkspaceInvite,
  type WorkspaceMember,
  type WorkspaceShareLink,
} from '@bangle.io/supabase';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@bangle.io/ui-components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMarkPathPrivate } from './use-private-paths';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  currentNotePath: string;
};

type ShareMode = 'workspace-edit' | 'note-edit' | 'reader-link';

const SHARE_OPTIONS: {
  mode: ShareMode;
  title: string;
  description: string;
  action: string;
}[] = [
  {
    mode: 'workspace-edit',
    title: 'Edit the Whole Workspace',
    description: 'Users can edit any note in this workspace.',
    action: 'Copy Invite Link',
  },
  {
    mode: 'note-edit',
    title: 'Edit This Note Only',
    description: 'Users can only edit this note.',
    action: 'Copy Invite Link',
  },
  {
    mode: 'reader-link',
    title: 'Read-Only Link',
    description:
      'Anyone with the link can read this note. No account required.',
    action: 'Copy Reader Link',
  },
];

function noteTitle(path: string) {
  const base = path.split('/').pop() || path;
  return base.replace(/\.md$/i, '') || path;
}

function inviteSummary(inv: WorkspaceInvite) {
  const scopeLabel = inv.scope === 'item' ? 'This note' : 'Whole workspace';
  const roleLabel = inv.role === 'viewer' ? 'View (legacy)' : 'Can edit';
  return {
    title: scopeLabel,
    detail: `${roleLabel} · ${inv.use_count}/${inv.max_uses} used`,
  };
}

function memberAccess(role: string, projectWide: boolean) {
  if (role === 'owner') return 'Owner';
  if (role === 'viewer') return 'View only (legacy)';
  if (role === 'editor') {
    return projectWide ? 'Whole workspace' : 'Selected notes';
  }
  return role;
}

type ManageTab = 'people' | 'invites' | 'links';

export function SharePanel({
  open,
  onOpenChange,
  workspaceId,
  currentNotePath,
}: Props) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<ShareMode | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [manageTab, setManageTab] = useState<ManageTab>('people');

  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileRow>>({});
  const [invites, setInvites] = useState<WorkspaceInvite[]>([]);
  const [links, setLinks] = useState<WorkspaceShareLink[]>([]);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [createdKind, setCreatedKind] = useState<ShareMode | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [notePrivate, setNotePrivate] = useState(false);
  const markPathPrivate = useMarkPathPrivate();

  const noteName = useMemo(() => noteTitle(currentNotePath), [currentNotePath]);

  const clearCreated = useCallback(() => {
    setCreatedUrl(null);
    setCreatedKind(null);
    setCreatedId(null);
  }, []);

  const reload = useCallback(async () => {
    setError(null);
    try {
      const membership = await getMyMembership(workspaceId);
      setIsOwner(membership?.role === 'owner');
      const memberRows = await listMembers(workspaceId);
      setMembers(memberRows);
      const profileMap = await fetchProfilesByIds(
        memberRows.map((m) => m.user_id),
      );
      setProfiles(profileMap);
      if (membership?.role === 'owner') {
        const [nextInvites, nextLinks, nextPrivate] = await Promise.all([
          listInvites(workspaceId),
          listShareLinks(workspaceId),
          getFilePrivate(workspaceId, currentNotePath),
        ]);
        setInvites(nextInvites);
        setLinks(nextLinks);
        setNotePrivate(nextPrivate);
      } else {
        setInvites([]);
        setLinks([]);
        setNotePrivate(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sharing');
    }
  }, [workspaceId, currentNotePath]);

  useEffect(() => {
    if (!open) {
      setError(null);
      setBusy(null);
      setCopied(null);
      clearCreated();
      setManageOpen(false);
      setManageTab('people');
      return;
    }
    void reload();
  }, [open, reload, clearCreated]);

  async function copyText(key: string, text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setError('Could not copy');
    }
  }

  async function handleRevokeInvite(inviteId: string) {
    setError(null);
    setInvites((prev) => prev.filter((inv) => inv.id !== inviteId));
    if (createdId === inviteId) clearCreated();
    try {
      await revokeInvite(inviteId);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Revoke failed');
      await reload();
    }
  }

  async function handleRevokeLink(linkId: string) {
    setError(null);
    setLinks((prev) => prev.filter((link) => link.id !== linkId));
    if (createdId === linkId) clearCreated();
    try {
      await revokeShareLink(linkId);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Revoke failed');
      await reload();
    }
  }

  async function handleShare(mode: ShareMode) {
    setBusy(mode);
    setError(null);
    try {
      if (mode === 'reader-link') {
        // Server reuses the single active reader link for this note.
        const link = await createShareLink({
          workspaceId,
          itemPath: currentNotePath,
        });
        const url = `${window.location.origin}/r/${link.token}`;
        setCreatedUrl(url);
        setCreatedKind(mode);
        setCreatedId(link.id);
        setLinks((prev) => [
          link,
          ...prev.filter(
            (row) => row.id !== link.id && row.item_path !== currentNotePath,
          ),
        ]);
        await copyText('created', url);
        await reload();
        return;
      }

      const invite = await createInvite({
        workspaceId,
        scope: mode === 'note-edit' ? 'item' : 'project',
        itemPath: mode === 'note-edit' ? currentNotePath : undefined,
      });
      const url = `${window.location.origin}/join/${invite.code}`;
      setCreatedUrl(url);
      setCreatedKind(mode);
      setCreatedId(invite.id);
      setInvites((prev) => [
        invite,
        ...prev.filter((row) => {
          if (row.id === invite.id) return false;
          if (mode === 'workspace-edit') {
            return row.scope !== 'project';
          }
          return !(row.scope === 'item' && row.item_path === currentNotePath);
        }),
      ]);
      await copyText('created', url);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Share failed');
    } finally {
      setBusy(null);
    }
  }

  async function handleSetPrivate(next: boolean) {
    setBusy('workspace-edit');
    setError(null);
    try {
      await setFilePrivate(workspaceId, currentNotePath, next);
      markPathPrivate(workspaceId, currentNotePath, next);
      setNotePrivate(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update privacy');
    } finally {
      setBusy(null);
    }
  }

  const linksForThisNote = links.filter(
    (link) => link.item_path === currentNotePath,
  );
  const linksForOtherNotes = links.filter(
    (link) => link.item_path !== currentNotePath,
  );
  const openCount = invites.length + links.length;
  const createdLabel =
    createdKind === 'workspace-edit'
      ? 'Workspace edit invite'
      : createdKind === 'note-edit'
        ? 'Note edit invite'
        : createdKind === 'reader-link'
          ? 'Reader link'
          : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        overlayClassName="bg-transparent supports-backdrop-filter:backdrop-blur-none"
      >
        <DialogHeader>
          <DialogTitle>Share “{noteName}”</DialogTitle>
        </DialogHeader>

        {error && <p className="text-destructive text-sm">{error}</p>}

        {!isOwner ? (
          <p className="text-muted-foreground text-sm">
            Only the workspace owner can share notes from this workspace.
          </p>
        ) : (
          <div className="sn-share-section">
            <ul className="sn-share-options">
              {SHARE_OPTIONS.map((option) => (
                <li key={option.mode} className="sn-share-option">
                  <div className="sn-share-option-copy">
                    <strong>{option.title}</strong>
                    <p>{option.description}</p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant={
                      option.mode === 'workspace-edit' ? 'default' : 'secondary'
                    }
                    disabled={busy !== null}
                    onClick={() => void handleShare(option.mode)}
                  >
                    {busy === option.mode ? 'Working…' : option.action}
                  </Button>
                </li>
              ))}
            </ul>

            {createdUrl && createdLabel && createdId && (
              <div className="sn-share-success" role="status">
                <div className="sn-share-success-head">
                  <strong>{createdLabel} ready</strong>
                  <span>
                    {copied === 'created' ? 'Copied to clipboard' : ''}
                  </span>
                </div>
                <div className="sn-share-created">
                  <Input readOnly value={createdUrl} aria-label="Share link" />
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => void copyText('created', createdUrl)}
                  >
                    {copied === 'created' ? 'Copied' : 'Copy'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      if (createdKind === 'reader-link') {
                        void handleRevokeLink(createdId);
                      } else {
                        void handleRevokeInvite(createdId);
                      }
                    }}
                  >
                    Revoke
                  </Button>
                </div>
              </div>
            )}

            <section className="sn-share-privacy">
              <label className="sn-share-check">
                <input
                  type="checkbox"
                  checked={notePrivate}
                  disabled={busy !== null}
                  onChange={(e) => void handleSetPrivate(e.target.checked)}
                />
                Private Note
              </label>
              <p className="text-muted-foreground text-sm">
                Hidden from workspace editors unless invited to this note.
              </p>
            </section>

            <div className="sn-share-manage-shell">
              <button
                type="button"
                className="sn-share-manage-toggle"
                aria-expanded={manageOpen}
                onClick={() => setManageOpen((value) => !value)}
              >
                <span>
                  {manageOpen ? 'Hide access' : 'Manage access'}
                  {!manageOpen && openCount > 0 ? (
                    <span className="sn-share-manage-count">{openCount}</span>
                  ) : null}
                </span>
                <span className="sn-share-manage-caret" aria-hidden>
                  {manageOpen ? '▴' : '▾'}
                </span>
              </button>

              {manageOpen && (
                <div className="sn-share-manage">
                  <div
                    className="sn-share-manage-tabs"
                    role="tablist"
                    aria-label="Manage access"
                  >
                    {(
                      [
                        {
                          id: 'people' as const,
                          label: 'People',
                          count: members.length,
                        },
                        {
                          id: 'invites' as const,
                          label: 'Invites',
                          count: invites.length,
                        },
                        {
                          id: 'links' as const,
                          label: 'Links',
                          count: links.length,
                        },
                      ] as const
                    ).map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={manageTab === tab.id}
                        className={
                          manageTab === tab.id
                            ? 'sn-share-manage-tab is-active'
                            : 'sn-share-manage-tab'
                        }
                        onClick={() => setManageTab(tab.id)}
                      >
                        {tab.label}
                        {tab.count > 0 ? (
                          <span className="sn-share-manage-count">
                            {tab.count}
                          </span>
                        ) : null}
                      </button>
                    ))}
                  </div>

                  {manageTab === 'people' && (
                    <section className="sn-share-manage-pane">
                      {members.length === 0 ? (
                        <p className="sn-share-empty">No people yet.</p>
                      ) : (
                        <ul className="sn-share-list">
                          {members.map((m) => {
                            const profile = profiles[m.user_id];
                            const label = personLabel({
                              display_name: profile?.display_name,
                              email: profile?.email,
                            });
                            return (
                              <li key={m.user_id} className="sn-share-row-card">
                                <div className="sn-share-row-copy">
                                  <strong>{label}</strong>
                                  <span>
                                    {memberAccess(m.role, m.project_wide)}
                                  </span>
                                </div>
                                {m.role !== 'owner' && (
                                  <div className="sn-share-actions">
                                    {m.role === 'editor' && (
                                      <Button
                                        type="button"
                                        size="xs"
                                        variant="secondary"
                                        onClick={() => {
                                          void setMemberProjectWide(
                                            workspaceId,
                                            m.user_id,
                                            !m.project_wide,
                                          )
                                            .then(reload)
                                            .catch((err) =>
                                              setError(
                                                err instanceof Error
                                                  ? err.message
                                                  : 'Access update failed',
                                              ),
                                            );
                                        }}
                                      >
                                        {m.project_wide
                                          ? 'Limit to notes'
                                          : 'Whole workspace'}
                                      </Button>
                                    )}
                                    <Button
                                      type="button"
                                      size="xs"
                                      variant="destructive"
                                      onClick={() => {
                                        setMembers((prev) =>
                                          prev.filter(
                                            (row) => row.user_id !== m.user_id,
                                          ),
                                        );
                                        void removeMember(
                                          workspaceId,
                                          m.user_id,
                                        )
                                          .then(reload)
                                          .catch((err) => {
                                            setError(
                                              err instanceof Error
                                                ? err.message
                                                : 'Remove failed',
                                            );
                                            void reload();
                                          });
                                      }}
                                    >
                                      Remove
                                    </Button>
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </section>
                  )}

                  {manageTab === 'invites' && (
                    <section className="sn-share-manage-pane">
                      {invites.length === 0 ? (
                        <p className="sn-share-empty">No open invites.</p>
                      ) : (
                        <ul className="sn-share-list">
                          {invites.map((inv) => {
                            const summary = inviteSummary(inv);
                            return (
                              <li key={inv.id} className="sn-share-row-card">
                                <div className="sn-share-row-copy">
                                  <strong>{summary.title}</strong>
                                  <span>{summary.detail}</span>
                                </div>
                                <div className="sn-share-actions">
                                  <Button
                                    type="button"
                                    size="xs"
                                    variant="secondary"
                                    onClick={() =>
                                      void copyText(
                                        inv.id,
                                        `${window.location.origin}/join/${inv.code}`,
                                      )
                                    }
                                  >
                                    {copied === inv.id ? 'Copied' : 'Copy'}
                                  </Button>
                                  <Button
                                    type="button"
                                    size="xs"
                                    variant="destructive"
                                    onClick={() =>
                                      void handleRevokeInvite(inv.id)
                                    }
                                  >
                                    Revoke
                                  </Button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </section>
                  )}

                  {manageTab === 'links' && (
                    <section className="sn-share-manage-pane">
                      {links.length === 0 ? (
                        <p className="sn-share-empty">No reader links yet.</p>
                      ) : (
                        <ul className="sn-share-list">
                          {linksForThisNote.map((link) => (
                            <li key={link.id} className="sn-share-row-card">
                              <div className="sn-share-row-copy">
                                <strong>
                                  {link.title_cache ||
                                    noteTitle(link.item_path || '')}
                                </strong>
                                <span>This note</span>
                              </div>
                              <div className="sn-share-actions">
                                <Button
                                  type="button"
                                  size="xs"
                                  variant="secondary"
                                  onClick={() =>
                                    void copyText(
                                      link.id,
                                      `${window.location.origin}/r/${link.token}`,
                                    )
                                  }
                                >
                                  {copied === link.id ? 'Copied' : 'Copy'}
                                </Button>
                                <Button
                                  type="button"
                                  size="xs"
                                  variant="secondary"
                                  onClick={() => {
                                    void refreshShareLink(link.id)
                                      .then(reload)
                                      .catch((err) =>
                                        setError(
                                          err instanceof Error
                                            ? err.message
                                            : 'Refresh failed',
                                        ),
                                      );
                                  }}
                                >
                                  Refresh
                                </Button>
                                <Button
                                  type="button"
                                  size="xs"
                                  variant="destructive"
                                  onClick={() => void handleRevokeLink(link.id)}
                                >
                                  Revoke
                                </Button>
                              </div>
                            </li>
                          ))}
                          {linksForOtherNotes.map((link) => (
                            <li key={link.id} className="sn-share-row-card">
                              <div className="sn-share-row-copy">
                                <strong>
                                  {link.title_cache ||
                                    noteTitle(link.item_path || '')}
                                </strong>
                                <span>Other note</span>
                              </div>
                              <div className="sn-share-actions">
                                <Button
                                  type="button"
                                  size="xs"
                                  variant="secondary"
                                  onClick={() =>
                                    void copyText(
                                      link.id,
                                      `${window.location.origin}/r/${link.token}`,
                                    )
                                  }
                                >
                                  {copied === link.id ? 'Copied' : 'Copy'}
                                </Button>
                                <Button
                                  type="button"
                                  size="xs"
                                  variant="destructive"
                                  onClick={() => void handleRevokeLink(link.id)}
                                >
                                  Revoke
                                </Button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
